import DashboardContainer from "components/dashboard/DashboardContainer";
import { Banner } from "components/DesignSystem/Banner/Banner";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { AdminMissingInfo } from "./AdminMissingInfo";
import { StateDeadlineRulesTab } from "./StateDeadlineRulesTab";
import { Button } from "components/DesignSystem/Button/Button";
import { ArrowRightUpV2 } from "components/icons/ArrowUpRightV2";
import { Chip } from "components/DesignSystem/Chips/Chips";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { TableUI } from "components/design/TableUI";
import { DateFilter } from "components/Filters/DateFilter";
import { MultiGroupFilter } from "components/Filters/MultiGroupFilter";
import TaskTabBar from "components/tasks/taskTabBar";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { useFilters } from "hooks/useFilter";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { ConditionalLink } from "components/conditionalLink";
import { FilingDeadline, useGetFilingsDashboardQuery } from "store/apis/alarm";
import { useMemo, useState } from "react";
import { formatDateByViewType } from "utils/formatDate";
import { PERIOD } from "constants/revenueMetrics";
import {
  CUSTOM,
  DEADLINE_DATE_PERIODS,
  DEADLINE_STATUS_MAP,
  DUE_IN_30_DAYS,
  DUE_IN_60_DAYS,
  DUE_IN_90_DAYS,
  STATUS_OPTIONS,
  STATUS_TAG_MAP,
} from "constants/deadlinesTracker";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { useGetAllActiveEntitiesQuery } from "store/apis/serviceTeam";
import { getSeasonsFrom } from "utils/getAllSeason";
import { useGetBaseTaskTemplatesQuery } from "store/apis/getTemplates";

type FilterValues = {
  START_DATE: string;
  END_DATE: string;
  SELECT_PERIOD: string;
  STATUS: string[];
  GROUPS: string[];
  ENTITY_UUID: string;
  FILING_NAME: string[];
  SEASON: string[];
  PAGE_NUM: string;
};

const DEFAULT_START = dayjs().format(YYYY_MM_DD);
const DEFAULT_END = dayjs().add(60, "day").format(YYYY_MM_DD);

const initialFilterValues: FilterValues = {
  START_DATE: DEFAULT_START,
  END_DATE: DEFAULT_END,
  SELECT_PERIOD: "",
  STATUS: ["PAYMENT_PENDING"],
  GROUPS: [],
  ENTITY_UUID: "",
  FILING_NAME: [],
  SEASON: [],
  PAGE_NUM: "1",
};

const FilingStatusTag = ({ status }: { status: string }) => {
  const config = STATUS_TAG_MAP[status] || {
    label: status.replace(/_/g, " "),
    tagType: "gray",
  };
  return (
    <Tag icon={false} tagType={config.tagType as any} rounded>
      {config.label}
    </Tag>
  );
};

const DeadlineCell = ({
  deadline,
  deadlineStatus,
}: {
  deadline: string | null;
  deadlineStatus: string | null;
}) => {
  const formatted = deadline ? dayjs(deadline).format(DISPLAY_DATE) : "—";
  const statusConfig = deadlineStatus
    ? DEADLINE_STATUS_MAP[deadlineStatus]
    : null;

  return (
    <div className="t-flex t-flex-col t-gap-0.5">
      <span className="t-text-text-30 t-text-body-sm">{formatted}</span>
      {statusConfig && (
        <div className="t-flex t-items-center t-gap-1">
          <span className={`t-text-[10px] ${statusConfig.color}`}>●</span>
          <span className="t-text-body-sm t-text-text-30">
            {statusConfig.label}
          </span>
        </div>
      )}
    </div>
  );
};

const ActionButton = ({ filing }: { filing: FilingDeadline }) => {
  let to = "";

  if (
    filing.source === "task" &&
    !(filing.filing === "Ad Hoc" && filing.status === "PAYMENT_PENDING")
  ) {
    to = `/tax/filings/${filing.uuid}`;
  } else {
    to = `/admin/crm/${filing.group_uuid}/new-filings?FILING_STATUS=ALL`;
  }

  return (
    <ConditionalLink to={to}>
      <Button size="xsmall" customType="ghost_icon">
        <ArrowRightUpV2 />
      </Button>
    </ConditionalLink>
  );
};

const createColumn = createColumnHelper<FilingDeadline>();

const columns = [
  createColumn.accessor("entity", {
    header: "Entity and Group",
    size: 20,
    cell: (info) => (
      <div className="t-flex t-flex-col">
        <ConditionalLink
          to={`/admin/crm/${info.row.original.group_uuid}/entity/${info.row.original.entity_uuid}`}
          className="t-text-purple t-text-body-sm"
        >
          {info.getValue()}
        </ConditionalLink>
        <span className="t-text-text-30 t-text-body-sm">
          {info.row.original.group}
        </span>
      </div>
    ),
  }),
  createColumn.accessor("filing", {
    header: "Filing",
    size: 35,
    cell: (info) => (
      <span className="t-text-text-30 t-text-body-sm">
        {info.getValue() || "—"}
      </span>
    ),
  }),
  createColumn.accessor("deadline", {
    header: "Deadline",
    size: 20,
    cell: (info) => (
      <DeadlineCell
        deadline={info.getValue()}
        deadlineStatus={info.row.original.deadline_status}
      />
    ),
  }),
  createColumn.accessor("status", {
    header: "Status",
    size: 18,
    cell: (info) => <FilingStatusTag status={info.getValue()} />,
  }),
  createColumn.display({
    id: "actions",
    header: "",
    size: 5,
    cell: (info) => <ActionButton filing={info.row.original} />,
  }),
];

const formatDateRangeLabel = (startDate: string, endDate: string) => {
  if (startDate && endDate) {
    return `${formatDateByViewType(
      startDate,
      PERIOD.TOTALS
    )} - ${formatDateByViewType(endDate, PERIOD.TOTALS)}`;
  }
  if (startDate)
    return `Since ${formatDateByViewType(startDate, PERIOD.TOTALS)}`;
  return `Till ${formatDateByViewType(endDate, PERIOD.TOTALS)}`;
};

const DUE_IN_DAYS_MAP: Record<string, number> = {
  [DUE_IN_30_DAYS]: 30,
  [DUE_IN_60_DAYS]: 60,
  [DUE_IN_90_DAYS]: 90,
};

const SEASONS = getSeasonsFrom(2020, dayjs().year() + 1).map((s) => ({
  label: String(s),
  value: String(s),
}));

const EntityFilter = ({
  selectedGroupUuids,
  selectedEntityUuid,
  onEntityChange,
}: {
  selectedGroupUuids: string[];
  selectedEntityUuid: string;
  onEntityChange: (uuid: string) => void;
}) => {
  const { data: allEntities = [] } = useGetAllActiveEntitiesQuery(undefined, {
    skip: selectedGroupUuids.length === 0,
  });

  if (selectedGroupUuids.length === 0) {
    return (
      <span className="t-text-body-sm t-text-text-30">
        Select a company group first
      </span>
    );
  }

  const entities = allEntities.filter((e) =>
    selectedGroupUuids.includes(e.company_group.uuid)
  );

  return (
    <div className="t-flex t-flex-col t-gap-3 t-overflow-y-auto t-max-h-full t-w-full">
      {entities.map((entity) => (
        <Checkbox
          key={entity.uuid}
          name={entity.uuid}
          label={entity.registered_name}
          checked={selectedEntityUuid === entity.uuid}
          onChange={(e) => onEntityChange(e.target.checked ? entity.uuid : "")}
        />
      ))}
      {entities.length === 0 && (
        <span className="t-text-body-sm t-text-text-30">No entities found</span>
      )}
    </div>
  );
};

const FilingNameFilter = ({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (names: string[]) => void;
}) => {
  const [search, setSearch] = useState("");
  const { data: templatesData } = useGetBaseTaskTemplatesQuery({});

  const allTemplates = useMemo(() => {
    if (!templatesData) return [];
    return Object.values(templatesData).flat();
  }, [templatesData]);

  const filtered = search
    ? allTemplates.filter((t) =>
        t.title.toLowerCase().includes(search.toLowerCase())
      )
    : allTemplates;

  const toggle = (title: string, checked: boolean) => {
    const updated = checked
      ? [...selected, title]
      : selected.filter((n) => n !== title);
    onChange(updated);
  };

  return (
    <div className="t-flex t-flex-col t-gap-3 t-w-full">
      <Search
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search filing name"
        className="t-w-full"
      />
      <div className="t-flex t-flex-col t-gap-3 t-overflow-y-auto t-h-full">
        {filtered.map((template) => (
          <Checkbox
            key={template.uuid}
            name={template.uuid}
            label={template.title}
            checked={selected.includes(template.title)}
            onChange={(e) => toggle(template.title, e.target.checked)}
          />
        ))}
        {filtered.length === 0 && (
          <span className="t-text-body-sm t-text-text-30">
            No filings found
          </span>
        )}
      </div>
    </div>
  );
};

const SeasonFilter = ({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (seasons: string[]) => void;
}) => {
  const toggle = (value: string, checked: boolean) => {
    const updated = checked
      ? [...selected, value]
      : selected.filter((s) => s !== value);
    onChange(updated);
  };

  return (
    <div className="t-flex t-flex-col t-gap-3 t-overflow-y-auto t-h-full">
      {SEASONS.map((season) => (
        <Checkbox
          key={season.value}
          name={season.value}
          label={season.label}
          checked={selected.includes(season.value)}
          onChange={(e) => toggle(season.value, e.target.checked)}
        />
      ))}
    </div>
  );
};

const DeadlinesFilter = ({
  filter,
  updateFilter,
}: {
  filter: FilterValues;
  updateFilter: <K extends keyof FilterValues>(
    key: K,
    value: FilterValues[K]
  ) => void;
}) => {
  const onDateChange: Parameters<typeof DateFilter>[0]["updateFilter"] = (
    name,
    value
  ) => {
    if (!value) return;

    if (name === "SELECT_PERIOD") {
      updateFilter("SELECT_PERIOD", value);
      if (value !== CUSTOM) {
        const days = DUE_IN_DAYS_MAP[value];
        if (days) {
          updateFilter("START_DATE", dayjs().format(YYYY_MM_DD));
          updateFilter("END_DATE", dayjs().add(days, "day").format(YYYY_MM_DD));
        }
      }
      return;
    }

    if (name === "START_DATE") {
      updateFilter("START_DATE", value);
      updateFilter("SELECT_PERIOD", CUSTOM);
    }
    if (name === "END_DATE") {
      updateFilter("END_DATE", value);
      updateFilter("SELECT_PERIOD", CUSTOM);
    }
  };

  const dateValues = {
    SELECT_PERIOD: filter.SELECT_PERIOD,
    START_DATE: filter.START_DATE,
    END_DATE: filter.END_DATE,
  };

  const hasDateFilter = Boolean(filter.START_DATE || filter.END_DATE);
  const hasStatusFilter = filter.STATUS.length > 0;
  const hasGroupFilter = filter.GROUPS.length > 0;
  const hasEntityFilter = Boolean(filter.ENTITY_UUID);
  const hasFilingNameFilter = filter.FILING_NAME.length > 0;
  const hasSeasonFilter = filter.SEASON.length > 0;

  return (
    <Filter.Root
      defaultValue="group"
      capsule={
        <>
          {hasDateFilter && (
            <Chip filterType="deadline" isActive isRemovable={false}>
              {formatDateRangeLabel(filter.START_DATE, filter.END_DATE)}
            </Chip>
          )}
          {hasStatusFilter && (
            <Chip filterType="status" isActive isRemovable={false}>
              {`Status (${filter.STATUS.length})`}
            </Chip>
          )}
          {hasGroupFilter && (
            <Chip
              filterType="group"
              isActive
              onRemove={() => {
                updateFilter("GROUPS", []);
                updateFilter("ENTITY_UUID", "");
              }}
            >
              {`Group (${filter.GROUPS.length})`}
            </Chip>
          )}
          {hasEntityFilter && (
            <Chip
              filterType="entity"
              isActive
              onRemove={() => updateFilter("ENTITY_UUID", "")}
            >
              Entity
            </Chip>
          )}
          {hasFilingNameFilter && (
            <Chip
              filterType="filing"
              isActive
              onRemove={() => updateFilter("FILING_NAME", [])}
            >
              {`Filing (${filter.FILING_NAME.length})`}
            </Chip>
          )}
          {hasSeasonFilter && (
            <Chip
              filterType="season"
              isActive
              onRemove={() => updateFilter("SEASON", [])}
            >
              {`Season (${filter.SEASON.length})`}
            </Chip>
          )}
        </>
      }
    >
      <Filter.Portal>
        <Filter.List>
          <Filter.ListItem value="group">Company group</Filter.ListItem>
          <Filter.ListItem value="entity">Entity</Filter.ListItem>
          <Filter.ListItem value="filing">Filing name</Filter.ListItem>
          <Filter.ListItem value="season">Season</Filter.ListItem>
          <Filter.ListItem value="deadline">Deadline</Filter.ListItem>
          <Filter.ListItem value="status">Status</Filter.ListItem>
        </Filter.List>
        <Filter.Body value="group" block>
          <MultiGroupFilter
            values={filter}
            updateFilter={(name, value) => {
              updateFilter(name, value);
              updateFilter("ENTITY_UUID", "");
            }}
          />
        </Filter.Body>
        <Filter.Body value="entity" block>
          <EntityFilter
            selectedGroupUuids={filter.GROUPS}
            selectedEntityUuid={filter.ENTITY_UUID}
            onEntityChange={(uuid) => updateFilter("ENTITY_UUID", uuid)}
          />
        </Filter.Body>
        <Filter.Body value="filing" block>
          <FilingNameFilter
            selected={filter.FILING_NAME}
            onChange={(names) => updateFilter("FILING_NAME", names)}
          />
        </Filter.Body>
        <Filter.Body value="season" block>
          <SeasonFilter
            selected={filter.SEASON}
            onChange={(s) => updateFilter("SEASON", s)}
          />
        </Filter.Body>
        <Filter.Body value="deadline" block>
          <DateFilter
            datePeriod={DEADLINE_DATE_PERIODS}
            pickerType="date"
            skipDateUpdateOnPeriodChange
            skipMaxEndDate
            values={dateValues}
            updateFilter={onDateChange}
          />
        </Filter.Body>
        <Filter.Body value="status" block>
          <MultiSelectFilter
            options={STATUS_OPTIONS}
            selected={filter.STATUS}
            onChange={(vals) => updateFilter("STATUS", vals as string[])}
            isSearchable={false}
            isSelectAll={false}
          />
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};

const CRITICAL_DAYS = 30;
const TODAY = dayjs().format(YYYY_MM_DD);
const CRITICAL_END = dayjs().add(CRITICAL_DAYS, "day").format(YYYY_MM_DD);

const CriticalDeadlinesBanner = ({ onViewAll }: { onViewAll: () => void }) => {
  const { data } = useGetFilingsDashboardQuery({
    page_num: 1,
    deadline_from: TODAY,
    deadline_to: CRITICAL_END,
  });

  const count = data?.total_count;

  if (!count) return null;

  return (
    <Banner
      type="warning"
      title={
        <span className="t-text-subtext-sm t-text-text-60">
          You have {count} upcoming filing{count > 1 ? "s" : ""}
        </span>
      }
      description={
        <span className="t-text-body-sm">
          Review filings with deadlines approaching in the next {CRITICAL_DAYS}{" "}
          days.
        </span>
      }
      cta={
        <Button customType="link" size="small" onClick={onViewAll}>
          View filings
        </Button>
      }
    />
  );
};

type DashboardTab = "DEADLINES" | "MISSING_INFO" | "DEADLINE_RULES";

const DeadlinesContent = () => {
  const { update } = useUpdateQuery();
  const query = useQuery();
  const search = query.get("search") || "";

  const { values: filter, updateFilter } = useFilters<FilterValues>({
    initialValue: initialFilterValues,
    useQueryParams: true,
  });

  const pageNum = Number(filter.PAGE_NUM);

  const tableColumns = useMemo(() => columns, []);

  const { data, isLoading, isFetching } = useGetFilingsDashboardQuery({
    page_num: pageNum,
    search_term: search || undefined,
    status: filter.STATUS.length ? filter.STATUS : undefined,
    deadline_from: filter.START_DATE || undefined,
    deadline_to: filter.END_DATE || undefined,
    group_uuid: filter.GROUPS.length === 1 ? filter.GROUPS[0] : undefined,
    entity_uuid: filter.ENTITY_UUID || undefined,
    filing_name: filter.FILING_NAME.length ? filter.FILING_NAME : undefined,
    season: filter.SEASON.length ? filter.SEASON : undefined,
  });

  const tableData = useMemo(() => data?.filings || [], [data]);

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 5 },
  });

  return (
    <>
      <DashboardContainer.Header sticky>
        <div className="t-bg-surface t-flex t-flex-col t-gap-4">
          <CriticalDeadlinesBanner
            onViewAll={() => {
              updateFilter("START_DATE", TODAY);
              updateFilter("END_DATE", CRITICAL_END);
              updateFilter(
                "STATUS",
                STATUS_OPTIONS.map((o) => o.value)
              );
              updateFilter("PAGE_NUM", "1");
            }}
          />
          <div className="t-flex t-gap-4">
            <div className="t-flex t-gap-2 t-w-full t-items-center">
              <Search
                isLoading={isFetching}
                className="t-w-[18%] focus:t-w-3/6 t-transition-all t-self-start"
                defaultValue={search}
                onChange={(e) => {
                  updateFilter("PAGE_NUM", "1");
                  update({ query: "search", value: e.target.value });
                }}
                placeholder="Search"
              />
              <DeadlinesFilter filter={filter} updateFilter={updateFilter} />
            </div>
            <div className="t-flex t-justify-end t-self-start">
              <PaginationHandler
                page_num={pageNum}
                per_page={20}
                total_count={data?.total_count || 0}
                total_pages={data?.total_pages || 1}
                onPageNumChange={(page) =>
                  updateFilter("PAGE_NUM", String(page))
                }
                isLoading={isFetching}
              />
            </div>
          </div>
        </div>
      </DashboardContainer.Header>

      <TableUI table={table} isLoading={isLoading} />

      {!isLoading && tableData.length === 0 && (
        <div className="t-flex t-flex-col t-items-center t-justify-center t-h-72 t-text-text-60 t-gap-3">
          <p className="t-m-0 t-text-subtitle-sm">No deadlines found</p>
          <p className="t-m-0 t-text-body-sm">Try adjusting your filters.</p>
        </div>
      )}
    </>
  );
};

export const FilingDeadlinesDashboard = () => {
  const query = useQuery();
  const { update } = useUpdateQuery();
  const activeTab = (query.get("tab") as DashboardTab) || "DEADLINES";

  const setActiveTab = (tab: DashboardTab) =>
    update({ query: "tab", value: tab });

  return (
    <div className="t-w-full">
      <TaskTabBar defaultValue="admin/filings-deadlines-dashboard" />

      <DashboardContainer className="t-gap-4 t-px-5 t-pb-4">
        <DashboardContainer.Header>
          <ToggleGroup
            size="small"
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as DashboardTab)}
            className="t-w-[480px]"
          >
            <ToggleGroupItem value="DEADLINES">Deadlines</ToggleGroupItem>
            <ToggleGroupItem value="MISSING_INFO">Missing Info</ToggleGroupItem>
            <ToggleGroupItem value="DEADLINE_RULES">
              Deadline Rules
            </ToggleGroupItem>
          </ToggleGroup>
        </DashboardContainer.Header>

        {activeTab === "DEADLINES" && <DeadlinesContent />}
        {activeTab === "MISSING_INFO" && <AdminMissingInfo />}
        {activeTab === "DEADLINE_RULES" && <StateDeadlineRulesTab />}

        <div className="t-pb-20" />
      </DashboardContainer>
    </div>
  );
};
